import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { useModal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { Theme } from '@/utils/types';

import { ForgotPasswordModal } from '../components/forgot-password-modal';
import { LoginForm } from '../components/login-form';
import { LoginMethodCard } from '../components/login-method-card';
import { ResetPasswordModal } from '../components/reset-password-modal';
import { settings as loginThemeSettings } from '../utils/login-theme-settings';

/**
 * We have unique setting for the each of login method (wine, beer, ethanol).
 *
 * UI designer has used a different color scheme over the default, for the login view. :D
 * therefore, we have defined colors here separately.
 *
 */
const getLoginSettings = (method: Theme) => {
  return loginThemeSettings[method];
};

export const LoginPage = () => {
  // global hooks for translation and theme
  const { t } = useTranslation();
  const { setTheme, current }: any = useTheme();

  const { resetToken = '' } = useParams();

  const [searchParams, _] = useSearchParams();

  // keep track of the theme state to change the appearance of login page
  const [loginMethod, setLoginMethod] = useState<Theme>(Theme.Wine);

  // hook for the password forgot modal
  const { isOpen, toggle } = useModal();
  const { isOpen: isOpenResetPass, toggle: toggleResetPassModal } = useModal();

  // theme based login settings
  const settings = useMemo(() => getLoginSettings(loginMethod), [loginMethod]);

  const handleLoginMethodChange = (t: Theme) => {
    // change global theme, which will change the logo accordingly
    setTheme(t);
    setLoginMethod(t);
  };

  useEffect(() => {
    // open password forgot modal if needed
    const shouldOpenPasswordModal = searchParams.get('password');
    if (shouldOpenPasswordModal) toggle();

    // when redirected with ?method=wine|beer, right method will be selected in the login page
    const method = searchParams.get('method');
    if (method) {
      if (method === Theme.Wine) handleLoginMethodChange(Theme.Wine);
      if (method === Theme.Beer) handleLoginMethodChange(Theme.Beer);
      if (method === Theme.Ethanol) handleLoginMethodChange(Theme.Ethanol);
      return;
    }

    // when user logout, we need to keep the right method selected in the login page
    if (current) {
      handleLoginMethodChange(current);
      return;
    }
  }, []);

  // if reset token exists in URI, open password reset modal
  useEffect(() => {
    if (resetToken) {
      toggleResetPassModal();
    }
  }, [resetToken]);

  // list of login method cards, component will exclude selected login method card from the list
  const LoginMethodCards = () => {
    return Object.entries(loginThemeSettings)
      .filter(([theme, _]) => theme !== loginMethod)
      .map(([theme, config]) => {
        return (
          <LoginMethodCard
            key={theme}
            thumbnailImage={config.loginCardThumbnail}
            title={config.title}
            onClick={() => handleLoginMethodChange(theme as Theme)}
            buttonColor={config.loginMethodButtonColor}
          />
        );
      });
  };

  return (
    <>
      <ForgotPasswordModal isOpen={isOpen} toggle={toggle} />
      <ResetPasswordModal
        token={resetToken}
        isOpen={isOpenResetPass}
        toggle={toggleResetPassModal}
      />

      <div className="relative mb-20 md:mb-36">
        <div className="flex flex-col mt-10 lg:px-5 lg:w-8/12 lg:mt-[8.75rem] xl:px-10 xl:w-7/12 xl:mt-[8.75rem] 2xl:px-[12.5rem] 2xl:w-3/4 2xl:mt-[8.75rem] gap-4">
          {/* start: login control container */}
          <div className={`${settings.backgroundColor} rounded-[40px] md:p-20 p-10`}>
            <h1 className="text-5xl font-bold uppercase">{t('login.title')}</h1>

            {/* start: title devider */}
            <div className={`w-24 h-1 ${settings.titleDeviderColor} my-4`}></div>
            {/* end: title devider */}

            <h3 className="w-full text-lg font-medium leading-none md:w-10/12 ">
              {t(`login.description.${current}`)}
              <br />
            </h3>

            <div className="gap-6 pt-12">
              <LoginForm settings={settings} />
              <Button
                classnames={`block mt-6 ${settings.forgotPasswordLinkColor} font-medium z-30`}
                variant={ButtonVariant.Link}
                onClick={toggle}
              >
                {t('login.forgetPassword')}
              </Button>
            </div>
          </div>
          {/* end: login control container */}

          {/* start: login methods container */}
          <div className="grid sm:grid-cols-2 gap-4">{LoginMethodCards()}</div>
          {/* end: login methods container */}
        </div>

        {/* start: desktop background */}
        <div
          className={`absolute hidden lg:block right-0 h-full lg:w-[80%] xl:w-[82%] 2xl:w-9/12 xl:right-0 2xl:right-10 lg:-top-5 xl:-top-10 bg-wine bg-contain bg-no-repeat bg-right ${settings.backgroundImage}`}
        ></div>
        {/* end: desktop background */}

        {/* start: mobile background */}
        <div className="relative">
          <div
            className={`absolute top-[-30rem] sm:-top-96 right-0 sm:right-20 lg:hidden w-1/2 sm:w-1/2 h-full md:w-1/2 sm:h-[50%] md:h-[45%] ${settings.mobileCurve} bg-cover bg-no-repeat z-10`}
          ></div>
          <img
            className="w-full mt-10 px-12 lg:hidden"
            src={settings.mobileBackgroundImage}
            alt="Mobile Login Background"
          />
        </div>
        {/* end: mobile background */}
      </div>
    </>
  );
};
